package one.healthium.uscreen.ui.blocks

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlin.math.roundToInt
import one.healthium.base.preferences.AppPreferences
import one.healthium.base.utils.cmToFt
import one.healthium.base.utils.ftToIn
import one.healthium.base.utils.inToFt
import one.healthium.base.utils.kgToLb
import one.healthium.base.utils.lbToKg
import one.healthium.base.utils.toCm
import one.healthium.ui.compose.components.RulerOrientation
import one.healthium.ui.compose.components.RulerPicker
import one.healthium.ui.compose.components.rememberRulerPickerState
import one.healthium.uscreen.ui.util.clickableHapticOnPress
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.uscreen.domain.models.HorizontalRulerBlock
import one.healthium.uscreen.domain.models.MeasurementUnit
import one.healthium.uscreen.domain.models.RulerOption
import one.healthium.uscreen.domain.models.VerticalRulerBlock
import one.healthium.uscreen.ui.UScreenAnswer
import one.healthium.uscreen.ui.util.uscreenPadding
import org.koin.compose.koinInject

@Composable
fun UScreenVerticalRulerBlock(
    modifier: Modifier = Modifier,
    block: VerticalRulerBlock,
    answers: Map<String, UScreenAnswer>,
    onNumberChanged: (key: String, value: Float) -> Unit,
) {
    val appPreferences = koinInject<AppPreferences>()
    val isMetric by appPreferences.isMetricFlow.collectAsState()
    val answer = answers[block.key] as? UScreenAnswer.NumberAnswer
    val selectedUnit = resolveUnit(block.options, isMetric)
    val option = block.options.find { it.unit == selectedUnit }
        ?: block.options.firstOrNull()
        ?: return

    val initialDisplayValue = answer?.value?.let { fromMetric(it, option.unit) } ?: option.default
    val rulerState = rememberRulerPickerState(initialValue = initialDisplayValue, selectedUnit)
    var displayValue by remember(selectedUnit) { mutableStateOf(initialDisplayValue) }

    Box(
        modifier = modifier
            .fillMaxWidth()
            .uscreenPadding(block.padding)
            .testTag("uscreen_vertical_ruler"),
    ) {
        RulerPicker(
            state = rulerState,
            min = option.min,
            max = option.max,
            majorEvery = option.major,
            step = option.minor,
            orientation = RulerOrientation.Vertical,
            indicatorColor = AppTheme.colors.primary,
            tickColor = AppTheme.uscreenStyle.rulerTickColor,
            majorTickColor = AppTheme.uscreenStyle.rulerMajorTickColor,
            labelColor = AppTheme.uscreenStyle.rulerLabelColor,
            labelFormatter = { value -> rulerLabelFormatter(value, option.unit) },
            indicatorLength = 110.dp,
            onValueChanged = { value ->
                displayValue = value
                onNumberChanged(block.key, toMetric(value, option.unit))
            },
            modifier = Modifier.fillMaxSize(),
        )

        Text(
            text = formatRulerValue(displayValue, option.unit),
            fontSize = 50.sp,
            fontWeight = FontWeight.Bold,
            letterSpacing = (-1).sp,
            color = AppTheme.uscreenStyle.pickerTextColor,
            modifier = Modifier
                .align(Alignment.CenterStart)
                .padding(start = 142.dp),
        )

        if (block.options.size > 1) {
            RulerUnitToggle(
                options = block.options,
                selectedUnit = selectedUnit,
                onUnitSelected = { newUnit ->
                    appPreferences.isMetric = newUnit.isMetric
                },
                modifier = Modifier.align(Alignment.TopCenter),
            )
        }
    }
}

@Composable
fun UScreenHorizontalRulerBlock(
    modifier: Modifier = Modifier,
    block: HorizontalRulerBlock,
    answers: Map<String, UScreenAnswer>,
    onNumberChanged: (key: String, value: Float) -> Unit,
) {
    val appPreferences = koinInject<AppPreferences>()
    val isMetric by appPreferences.isMetricFlow.collectAsState()
    val answer = answers[block.key] as? UScreenAnswer.NumberAnswer
    val selectedUnit = resolveUnit(block.options, isMetric)
    val option = block.options.find { it.unit == selectedUnit }
        ?: block.options.firstOrNull()
        ?: return

    val initialDisplayValue = answer?.value?.let { fromMetric(it, option.unit) } ?: option.default
    val rulerState = rememberRulerPickerState(initialValue = initialDisplayValue, selectedUnit)
    var displayValue by remember(selectedUnit) { mutableStateOf(initialDisplayValue) }

    Column(
        modifier = modifier
            .fillMaxSize()
            .uscreenPadding(block.padding)
            .testTag("uscreen_horizontal_ruler"),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        if (block.options.size > 1) {
            RulerUnitToggle(
                options = block.options,
                selectedUnit = selectedUnit,
                onUnitSelected = { newUnit ->
                    appPreferences.isMetric = newUnit.isMetric
                },
            )
        }

        Spacer(Modifier.weight(1f))

        Row(verticalAlignment = Alignment.Bottom) {
            Text(
                text = formatRulerValue(displayValue, option.unit),
                fontSize = 50.sp,
                fontWeight = FontWeight.Bold,
                letterSpacing = (-1).sp,
                color = AppTheme.uscreenStyle.pickerTextColor,
            )
            Spacer(Modifier.width(8.dp))
            Text(
                text = option.label,
                fontSize = 22.sp,
                fontWeight = FontWeight.Medium,
                color = AppTheme.uscreenStyle.pickerTextColor,
                modifier = Modifier.padding(bottom = 6.dp),
            )
        }

        Spacer(Modifier.height(24.dp))

        RulerPicker(
            state = rulerState,
            min = option.min,
            max = option.max,
            majorEvery = option.major,
            step = option.minor,
            orientation = RulerOrientation.Horizontal,
            ticksFromEnd = true,
            indicatorLength = 94.dp,
            indicatorColor = AppTheme.colors.primary,
            tickColor = AppTheme.uscreenStyle.rulerTickColor,
            majorTickColor = AppTheme.uscreenStyle.rulerMajorTickColor,
            labelColor = AppTheme.uscreenStyle.rulerLabelColor,
            labelFormatter = { value -> rulerLabelFormatter(value, option.unit) },
            onValueChanged = { value ->
                displayValue = value
                onNumberChanged(block.key, toMetric(value, option.unit))
            },
            modifier = Modifier
                .fillMaxWidth()
                .weight(2f),
        )
    }
}

private fun resolveUnit(
    options: List<RulerOption>,
    isMetric: Boolean,
): MeasurementUnit? {
    return options.find { it.unit.isMetric == isMetric }?.unit
        ?: options.firstOrNull()?.unit
}

private fun toMetric(displayValue: Int, displayUnit: MeasurementUnit): Float = when (displayUnit) {
    MeasurementUnit.Cm -> displayValue.toFloat()
    MeasurementUnit.Ft -> displayValue.inToFt().toCm()
    MeasurementUnit.Kg -> displayValue.toFloat()
    MeasurementUnit.Lbs -> displayValue.toFloat().lbToKg()
}

private fun fromMetric(metricValue: Float, displayUnit: MeasurementUnit): Int = when (displayUnit) {
    MeasurementUnit.Cm -> metricValue.roundToInt()
    MeasurementUnit.Ft -> metricValue.cmToFt().ftToIn()
    MeasurementUnit.Kg -> metricValue.roundToInt()
    MeasurementUnit.Lbs -> metricValue.kgToLb().roundToInt()
}

private val UnitToggleUnselectedBg = Color(0xFFF8F6F4)
private val OuterRadius = 19.dp
private val InnerRadius = 8.dp

@Composable
private fun RulerUnitToggle(
    options: List<RulerOption>,
    selectedUnit: MeasurementUnit?,
    onUnitSelected: (MeasurementUnit) -> Unit,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        options.forEachIndexed { index, option ->
            val isSelected = option.unit == selectedUnit
            val isFirst = index == 0
            val isLast = index == options.lastIndex
            val shape = RoundedCornerShape(
                topStart = if (isFirst) OuterRadius else InnerRadius,
                topEnd = if (isLast) OuterRadius else InnerRadius,
                bottomEnd = if (isLast) OuterRadius else InnerRadius,
                bottomStart = if (isFirst) OuterRadius else InnerRadius,
            )
            Surface(
                shape = shape,
                color = if (isSelected) AppTheme.colors.primary else UnitToggleUnselectedBg,
            ) {
                Box(
                    modifier = Modifier
                        .width(61.dp)
                        .height(38.dp)
                        .clickableHapticOnPress { onUnitSelected(option.unit) },
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        text = option.label,
                        fontSize = 17.sp,
                        fontWeight = FontWeight.Medium,
                        letterSpacing = (-0.17).sp,
                        color = if (isSelected) Color.White else Color.Black,
                    )
                }
            }
        }
    }
}

private fun formatRulerValue(value: Int, unit: MeasurementUnit): String {
    if (unit != MeasurementUnit.Ft) return value.toString()
    val feet = value / 12
    val inches = value % 12
    return "$feet'$inches\""
}

private fun rulerLabelFormatter(value: Int, unit: MeasurementUnit): String {
    if (unit != MeasurementUnit.Ft) return value.toString()
    return (value / 12).toString()
}
